const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';


// Utility function to get authentication headers
export const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  // Session-based authentication - no need for custom headers
  
  // Add user email header if available
  try {
    const userEmail = localStorage.getItem('kms_user_email');
    if (userEmail) {
      // Remove quotes if present
      let email = userEmail;
      if (email.startsWith('"') && email.endsWith('"')) {
        email = email.substring(1, email.length - 1);
      }
      headers['X-KMS-User-Email'] = email;
    }
  } catch (e) {
    console.error('Error adding user email to headers:', e);
  }
  
  return headers;
};

// Enhanced fetch function with auth error handling
const fetchWithAuth = async (url: string, options: RequestInit): Promise<Response> => {
  // FIRST check if we're in an auth flow - this is the most important check
  const authFlowInProgress = sessionStorage.getItem('auth_flow_in_progress') === 'true';
  
  // Disable auth flow debug logging to reduce console noise
  // Enable only when debugging auth flow issues: if (authFlowInProgress) {
  if (false && authFlowInProgress) {
    console.warn(`[Auth Flow Debug] Request to ${url} during active auth flow`);
  }
  
  try {
    const response = await fetch(url, options);
    
    // Handle authentication errors (401/403)
    if (response.status === 401 || response.status === 403) {
      console.warn('Authentication error detected:', response.status, response.statusText);
      
      // If we're in an auth flow, don't trigger any redirects or logout
      if (authFlowInProgress) {
        console.warn('In authentication flow - skipping auth redirect to prevent loops');
        return response;
      }
      
      // Check if this is a configuration request - if so, don't trigger redirects
      if (url.includes('/user-config/')) {
        console.warn('Auth error on configuration endpoint - not redirecting to avoid loops');
        return response;
      }
      
      // Try to parse the response to get more details
      try {
        const errorData = await response.clone().json();
        console.warn('Auth error details:', errorData);
        
        // Check if this is a login-required error
        if (errorData?.detail?.type === 'authentication_required' || 
            errorData?.message?.includes('authentication') ||
            errorData?.message?.includes('login')) {
          
          // Check if we're already on the login page or in an auth flow
          const isLoginPage = window.location.pathname === '/login';
          const isAuthCallback = window.location.pathname.includes('/auth/callback');
          
          // Prevent redirect loops - only redirect if not already on login or auth pages
          if (!isLoginPage && !isAuthCallback) {
            // Check if we've redirected recently
            const lastRedirectTime = sessionStorage.getItem('last_auth_redirect_time');
            const currentTime = Date.now();
            
            if (!lastRedirectTime || (currentTime - parseInt(lastRedirectTime)) > 10000) { // Increased from 5000 to 10000ms
              // Store redirect time to prevent loops
              sessionStorage.setItem('last_auth_redirect_time', currentTime.toString());
              
              // Clear any existing auth data
              localStorage.removeItem('kms_auth_user');
              localStorage.removeItem('kms_auth_check');
              
              // Redirect to login after a short delay
              console.log('Redirecting to login page due to auth error');
              setTimeout(() => {
                window.location.href = '/login';
              }, 100);
            } else {
              console.warn('Prevented redirect loop - redirected too recently');
            }
          } else {
            console.warn('Not redirecting to login - already on login or auth callback page');
          }
        }
      } catch (parseError) {
        console.error('Error parsing auth error response:', parseError);
        
        // Skip auth redirect if we're in an auth flow
        if (sessionStorage.getItem('auth_flow_in_progress')) {
          console.warn('In authentication flow - skipping auth redirect to prevent loops');
          return response;
        }
        
        // Simpler check to avoid redirect loops - only redirect if not already on login
        if (window.location.pathname !== '/login' && 
            !window.location.pathname.includes('/auth/callback')) {
          
          // Check for recent redirects
          const lastRedirectTime = sessionStorage.getItem('last_auth_redirect_time');
          const currentTime = Date.now();
          
          if (!lastRedirectTime || (currentTime - parseInt(lastRedirectTime)) > 10000) { // Increased from 5000 to 10000ms
            sessionStorage.setItem('last_auth_redirect_time', currentTime.toString());
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
          } else {
            console.warn('Prevented redirect loop - redirected too recently');
          }
        }
      }
    }
    
    return response;
  } catch (error) {
    console.error('Network error in fetchWithAuth:', error);
    throw error;
  }
};

// Utility function to construct fetch options with authentication
const getFetchOptions = (method: string = 'GET', body?: any): RequestInit => {
  const options: RequestInit = {
    method,
    headers: {
      ...getAuthHeaders(),
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache'
    },
    credentials: 'include', // Always include credentials for cookies
  };
  
  if (body) {
    options.body = JSON.stringify(body);
  }
  
  return options;
};

// Helper to check if a user is authenticated
export const checkAuthenticated = async (): Promise<boolean> => {
  try {
    console.debug('Checking authentication status...');
    const headers = getAuthHeaders();
    console.debug('Auth headers:', headers);
    
    const response = await fetch(`${API_BASE_URL}/auth/status`, {
      method: 'GET',
      headers: {
        ...headers,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      console.error('Auth check failed:', response.status, response.statusText);
      return false;
    }
    
    const data = await response.json();
    console.debug('Auth check result:', data);
    return data.success === true;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

export interface DocumentVersion {
  version_id: string;
  last_modified: string;
  is_latest: boolean;
  size: number;
}

export interface DocumentMetadata {
  title?: string;
  author?: string;
  created_date?: string;
  modified_date?: string;
  content_type?: string;
  file_type?: string;
  size?: number;
  page_count?: number;
}

export interface UploadResponse {
  file_name: string;
  version_id: string | null;
  success: boolean;
  message: string;
  metadata?: DocumentMetadata;
}

export interface VersionListResponse {
  file_name: string;
  versions: DocumentVersion[];
  count: number;
}

export interface PresignedUrlResponse {
  url: string;
  expiration: number;
  file_name: string;
}

export interface DocumentInfo {
  file_name: string;
  file_id: string;
  version_id?: string;
  last_modified: string;
  size: number;
  is_latest: boolean;
  content_type?: string;
  md5_checksum?: string;
  file_path?: string;
  source_folder_name?: string;
  source_folder_id?: string;
  source_collection?: string;
}

export interface DocumentListResponse {
  documents: DocumentInfo[];
  count: number;
}

export interface DocumentContentResponse {
  content: string;
}

export interface SearchResult {
  content: string;
  metadata: {
    file_name: string;
    file_id: string;
    title?: string;
    content_type?: string;
    [key: string]: any;
  };
  score: number;
}

export interface SearchResponse {
  results: SearchResult[];
  count: number;
  success: boolean;
  query: string;
  message?: string;
}

export interface EmbeddingResponse {
  success: boolean;
  message: string;
  collection_name?: string;
  chunk_count?: number;
  file_id?: string;
  file_name?: string;
  already_embedded?: boolean;
  unchanged?: boolean;
  version?: number;
}

export interface EmbeddingResult {
  file_name: string;
  file_id: string;
  success: boolean;
  message: string;
  chunk_count: number;
  already_embedded?: boolean;
  unchanged?: boolean;
  version?: number;
}

export interface EmbedAllResponse {
  success: boolean;
  message: string;
  results?: EmbeddingResult[];
  successful_count?: number;
  updated_count?: number;
  skipped_count?: number;
  failed_count?: number;
  total_count?: number;
}

export interface DocumentChangeCheckResult {
  file_id: string;
  file_name: string;
  changed: boolean;
  update_success?: boolean;
  error?: boolean;
  message: string;
}

export interface DocumentChangeResponse {
  success: boolean;
  message: string;
  documents_checked?: number;
  changes_detected?: number;
  update_success?: number;
  update_failed?: number;
  results?: DocumentChangeCheckResult[];
}

export interface EmbeddingStatsResponse {
  success: boolean;
  message: string;
  total_documents: number;
  embedded_documents: number;
  pending_documents: number;
  embedding_percentage: number;
  orphaned_embeddings: number;
  embedding_status?: Array<{
    file_id: string;
    file_name: string;
    file_path: string;
    is_embedded: boolean;
    last_embedded?: string;
    embedding_status?: string;
    chunk_count?: number;
    version?: number;
  }>;
}

export interface EmbedNewDocumentsResponse {
  success: boolean;
  message: string;
  results?: EmbeddingResult[];
  successful_count?: number;
  updated_count?: number;
  skipped_count?: number;
  failed_count?: number;
  total_count?: number;
}

export interface FindNewDocumentsResponse {
  success: boolean;
  message: string;
  new_documents: Array<{
    file_id: string;
    file_name: string;
    [key: string]: any;
  }>;
  total_drive_files?: number;
  total_embedded_files?: number;
}

export interface UserConfigurationRequest {
  folder_id: string;
  display_name: string;
  organization_name: string;
  is_default?: boolean;
}

export interface UserConfigurationResponse {
  config_id: string;
  folder_id: string;
  collection_name: string;
  display_name: string;
  organization_name: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  access_validated: boolean;
  user_email?: string;
  organization_id?: string;
}

export interface DatabaseFolderValidationResult {
  folder_accessible: boolean;
  folder_name?: string;
  folder_path?: string;
  error_message?: string;
}

export interface FolderInfo {
  folder_id: string;
  folder_name: string;
  folder_path: string;
  parent_id?: string;
}

export interface QARequest {
  query: string;
}

export interface QAResponse {
  answer: string;
  sources: Array<{
    content: string;
    metadata: Record<string, any>;
    score: number;
  }>;
  success: boolean;
}



export const DocumentService = {
  // Helper function to safely construct URLs with query parameters
  buildApiUrl(path: string, params?: Record<string, string>): string {
    // If API_BASE_URL is relative, construct URL without using new URL()
    let url = API_BASE_URL.startsWith('/') 
      ? `${window.location.origin}${API_BASE_URL}${path}`
      : `${API_BASE_URL}${path}`;
    
    // Add query parameters if provided
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }
    
    return url;
  },
  async listDocuments(forceRefresh: boolean = false): Promise<DocumentListResponse> {
    try {
      // First, check if we have a valid configuration before attempting to list documents
      try {
        const configurations = await UserConfigService.getConfigurations();
        const validConfigs = configurations.filter(config => 
          config && config.folder_id && config.folder_id.trim() !== ""
        );
        
        if (!validConfigs.length) {
          console.warn("No valid configurations with folder IDs found before attempting to list documents");
          throw {
            status: 404,
            message: "No active configuration with valid folder ID",
            isConfigurationError: true,
            response: { 
              status: 404, 
              data: { detail: "No active configuration with valid folder ID" } 
            }
          };
        }
      } catch (configError: any) {
        if (configError.isConfigurationError) {
          throw configError;
        }
        // If it's some other error, continue and let the document fetch try anyway
        console.warn("Error checking configurations, will try document fetch anyway:", configError);
      }
    
      const options = getFetchOptions();
      // Use the new endpoint that lists documents from all user configurations
      const response = await fetchWithAuth(`${API_BASE_URL}/documents/list-all${forceRefresh ? '?force_refresh=true' : ''}`, options);
      
      if (!response.ok) {
        // Special handling for 404 errors - treat as configuration issue
        if (response.status === 404) {
          console.warn("Document endpoint returned 404, treating as configuration issue");
          throw {
            status: 404,
            message: "Document endpoint not found - likely configuration issue",
            isConfigurationError: true,
            response: { 
              status: 404, 
              data: { detail: "No active configuration with valid folder ID" } 
            }
          };
        }
        
        const errorText = await response.text();
        let detail;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw {
          status: response.status,
          message: detail || response.statusText,
          response: { status: response.status, data: { detail } }
        };
      }
      
      return response.json();
    } catch (error) {
      console.error('Error listing documents:', error);
      throw error;
    }
  },

  async uploadDocument(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${errorText}`);
    }

    return response.json();
  },

  async extractMetadata(file: File): Promise<DocumentMetadata> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/documents/extract-metadata`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Metadata extraction failed: ${errorText}`);
    }

    return response.json();
  },

  async getDocumentVersions(fileName: string): Promise<VersionListResponse> {
    const response = await fetch(`${API_BASE_URL}/documents/versions/${fileName}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get versions: ${errorText}`);
    }

    return response.json();
  },

  async deleteDocument(fileName: string, versionId?: string): Promise<void> {
    const url = versionId 
      ? `${API_BASE_URL}/documents/${fileName}?version_id=${versionId}`
      : `${API_BASE_URL}/documents/${fileName}`;

    const response = await fetch(url, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Delete failed: ${errorText}`);
    }
  },

  async getPresignedDownloadUrl(fileName: string, versionId?: string): Promise<PresignedUrlResponse> {
    const url = new URL(`${API_BASE_URL}/documents/presigned-url`);
    url.searchParams.append('file_name', fileName);
    url.searchParams.append('operation', 'get_object');
    
    if (versionId) {
      url.searchParams.append('version_id', versionId);
    }

    const response = await fetch(url.toString(), { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get presigned URL: ${errorText}`);
    }

    return response.json();
  },

  async getPresignedUploadUrl(fileName: string): Promise<PresignedUrlResponse> {
    const url = new URL(`${API_BASE_URL}/documents/presigned-url`);
    url.searchParams.append('file_name', fileName);
    url.searchParams.append('operation', 'put_object');

    const response = await fetch(url.toString(), { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get presigned URL: ${errorText}`);
    }

    return response.json();
  },

  // Get a pre-signed URL for GET operations
  async getPresignedGetUrl(fileName: string, versionId?: string): Promise<PresignedUrlResponse> {
    const url = new URL(`${API_BASE_URL}/documents/presigned-url`);
    url.searchParams.append('file_name', fileName);
    url.searchParams.append('operation', 'get_object');
    
    if (versionId) {
      url.searchParams.append('version_id', versionId);
    }

    const response = await fetch(url.toString(), { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      // Try to parse backend error detail
      let detail = errorText;
      try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorText;
      } catch (e) { /* Ignore parsing error */ }
      throw new Error(`Failed to get presigned URL: ${detail}`);
    }

    return response.json();
  },

  getDownloadUrl(fileName: string, versionId?: string): string {
    const url = new URL(`${API_BASE_URL}/documents/download/${fileName}`);
    
    if (versionId) {
      url.searchParams.append('version_id', versionId);
    }

    return url.toString();
  },

  async getDocumentMetadata(fileNameOrId: string, versionId?: string): Promise<DocumentMetadata> {
    const url = new URL(`${API_BASE_URL}/documents/metadata/${fileNameOrId}`);
    
    if (versionId) {
      url.searchParams.append('version_id', versionId);
    }

    const response = await fetch(url.toString(), { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get metadata: ${errorText}`);
    }

    return response.json();
  },

  getViewUrl(fileName: string, versionId?: string): string {
    const params: Record<string, string> = {};
    
    if (versionId) {
      params.version_id = versionId;
    }

    return this.buildApiUrl(`/documents/view/${fileName}`, params);
  },

  async getDocumentContent(fileName: string, versionId?: string): Promise<DocumentContentResponse> {
    const encodedFileName = encodeURIComponent(fileName);
    const queryParams = versionId ? `?version_id=${versionId}` : '';
    const url = `${API_BASE_URL}/documents/${encodedFileName}/content${queryParams}`;

    const response = await fetch(url, { credentials: 'include' });

    if (!response.ok) {
      const errorText = await response.text();
      let detail = errorText;
      try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorText;
      } catch (e) { /* Ignore parsing error */ }
      throw new Error(`Failed to get document content: ${detail}`);
    }

    return response.json();
  },

  // Search embedded documents using vector search
  async searchDocuments(query: string, topK: number = 5): Promise<SearchResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/search`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query,
          top_k: topK
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorJson.message || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Search failed: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error searching documents:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async checkDocumentChanges(): Promise<DocumentChangeResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/check-document-changes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorJson.message || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to check document changes: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error checking document changes:', error);
      throw error;
    }
  },

  async getEmbeddingStats(): Promise<EmbeddingStatsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/embedding-stats`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          // Handle structured error responses
          if (errorJson.detail && typeof errorJson.detail === 'object') {
            detail = errorJson.detail.message || errorJson.detail.type || errorText;
          } else {
            detail = errorJson.detail || errorJson.message || errorText;
          }
        } catch (e) { /* Ignore parsing error */ }
        
        // Provide more specific error messages based on status code
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        } else if (response.status === 502) {
          throw new Error('Unable to connect to Google Drive service. Please try again later.');
        } else {
          throw new Error(`Failed to get embedding stats: ${detail}`);
        }
      }

      // Try to parse the response
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse embedding stats response:', responseText);
        throw new Error(`Invalid JSON response from server: ${responseText.substring(0, 200)}`);
      }
      
      // Validate the response structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from server');
      }
      
      // Ensure required fields are present with defaults
      const validatedData: EmbeddingStatsResponse = {
        success: data.success ?? false,
        message: data.message || '',
        total_documents: data.total_documents || 0,
        embedded_documents: data.embedded_documents || 0,
        pending_documents: data.pending_documents || 0,
        embedding_percentage: data.embedding_percentage || 0,
        orphaned_embeddings: data.orphaned_embeddings || 0,
        embedding_status: data.embedding_status || []
      };
      
      return validatedData;

    } catch (error) {
      console.error('Error fetching embedding stats:', error);
      // Ensure we always throw a proper Error with a string message
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedNewDocuments(): Promise<EmbedNewDocumentsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/embed-new`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorJson.message || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed new documents: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding new documents:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedSingleDocument(fileId: string, forceReembed: boolean = false): Promise<EmbeddingResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/embed`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_id: fileId,
          force_reembed: forceReembed
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorJson.message || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed document: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding document:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async checkDocumentChangesAsync(maxConcurrentTasks: number = 5): Promise<DocumentChangeResponse> {
    try {
      const params: Record<string, string> = {};
      if (maxConcurrentTasks !== 5) {
        params.max_concurrent_tasks = maxConcurrentTasks.toString();
      }
      
      const url = this.buildApiUrl('/vectorstore/check-document-changes-async', params);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorJson.message || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to check document changes asynchronously: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error checking document changes asynchronously:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedNewDocumentsAsync(maxConcurrentTasks: number = 5): Promise<EmbedNewDocumentsResponse> {
    try {
      const url = new URL(`${API_BASE_URL}/vectorstore/embed-new-async`);
      if (maxConcurrentTasks !== 20) { // Backend default is 20 for this endpoint
        url.searchParams.append('max_concurrent_tasks', maxConcurrentTasks.toString());
      }
      
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorJson.message || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed new documents asynchronously: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding new documents asynchronously:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedAllDocumentsAsync(forceReembed: boolean = false, maxConcurrentTasks: number = 5): Promise<EmbedAllResponse> {
    try {
      const url = new URL(`${API_BASE_URL}/vectorstore/embed-all-async`);
      if (forceReembed) {
        url.searchParams.append('force_reembed', 'true');
      }
      if (maxConcurrentTasks !== 5) {
        url.searchParams.append('max_concurrent_tasks', maxConcurrentTasks.toString());
      }
      
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorJson.message || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed all documents asynchronously: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding all documents asynchronously:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async findNewDocuments(): Promise<FindNewDocumentsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/vectorstore/find-new`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorJson.message || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to find new documents: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error finding new documents:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  },

  async embedAllDocuments(forceReembed: boolean = false): Promise<EmbedAllResponse> {
    try {
      const url = new URL(`${API_BASE_URL}/vectorstore/embed-all`);
      if (forceReembed) {
        url.searchParams.append('force_reembed', 'true');
      }
      
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          const errorJson = JSON.parse(errorText);
          detail = errorJson.detail || errorJson.message || errorText;
        } catch (e) { /* Ignore parsing error */ }
        throw new Error(`Failed to embed all documents: ${detail}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error embedding all documents:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error: ${String(error)}`);
    }
  }
};

export const UserConfigService = {
  // Cache for configuration data
  _configCache: null as UserConfigurationResponse[] | null,
  _configCacheTime: 0,
  _configCacheMaxAge: 60000, // 1 minute cache

  async getConfigurations(): Promise<UserConfigurationResponse[]> {
    // Check cache first
    const now = Date.now();
    const cacheAge = now - this._configCacheTime;
    
    if (this._configCache && cacheAge < this._configCacheMaxAge) {
      // Disable cache logging to reduce console noise
      // Enable only when debugging: console.log(`Using cached configuration data (${Math.round(cacheAge / 1000)}s old)`);
      return this._configCache;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations`, getFetchOptions());
      
      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 404) {
          // Only log in debug mode
          if (import.meta.env.DEV && false) { // Disabled to reduce console noise
            console.log('No configurations found for user');
          }
          const emptyResult: UserConfigurationResponse[] = [];
          // Cache empty result for a shorter time
          this._configCache = emptyResult;
          this._configCacheTime = now - (this._configCacheMaxAge - 10000); // Cache for only 10 seconds if empty
          return emptyResult;
        }
        
        const errorText = await response.text();
        console.error('Error response getting configurations:', response.status, errorText);
        throw new Error(`Failed to get configurations: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // Disable fresh data logging to reduce console noise
      // Enable only when debugging: console.log('Fresh configuration data loaded from API');
      
      // Update cache
      this._configCache = data;
      this._configCacheTime = now;
      
      return data;
    } catch (error) {
      console.error('Error getting configurations:', error);
      // Don't cache errors, but return cached data if available
      if (this._configCache) {
        // Disable stale data logging to reduce console noise
        // Enable only when debugging: console.log('Returning stale cached data due to error');
        return this._configCache;
      }
      throw error;
    }
  },

  // Clear cache when configurations change
  _clearConfigCache() {
    this._configCache = null;
    this._configCacheTime = 0;
  },

  async getDefaultConfiguration(): Promise<UserConfigurationResponse | null> {
    try {
      const options = getFetchOptions();
      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations/default`, options);

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorText = await response.text();
        throw new Error(`Failed to get default configuration: ${errorText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting default configuration:', error);
      throw error;
    }
  },

  async createConfiguration(request: UserConfigurationRequest): Promise<UserConfigurationResponse> {
    try {
      // Validate the request
      if (!request.folder_id || !request.display_name) {
        throw new Error('Missing required fields: folder_id and display_name are required');
      }

      console.log('Creating configuration:', request);

      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations`, getFetchOptions('POST', request));

      if (!response.ok) {
        let errorMessage = `Failed to create configuration: ${response.status} ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (errorData.detail.message) {
              errorMessage = errorData.detail.message;
            }
          }
        } catch (e) {
          // If we can't parse the error response, use the original message
          console.error('Failed to parse error response:', e);
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Configuration created successfully:', data);
      
      // Clear configuration cache since we've added a new config
      this._clearConfigCache();
      
      return data;
    } catch (error) {
      console.error('Error creating configuration:', error);
      throw error;
    }
  },

  async updateConfiguration(configId: string, request: UserConfigurationRequest): Promise<UserConfigurationResponse> {
    try {
      console.log('Updating configuration:', configId, request);

      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations/${configId}`, getFetchOptions('PUT', request));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response updating configuration:', response.status, errorText);
        throw new Error(`Failed to update configuration: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Configuration updated successfully:', data);
      
      // Clear configuration cache since we've modified a config
      this._clearConfigCache();
      
      return data;
    } catch (error) {
      console.error('Error updating configuration:', error);
      throw error;
    }
  },

  async deleteConfiguration(configId: string): Promise<void> {
    try {
      console.log('Deleting configuration:', configId);

      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/configurations/${configId}`, getFetchOptions('DELETE'));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response deleting configuration:', response.status, errorText);
        throw new Error(`Failed to delete configuration: ${response.status} ${response.statusText}`);
      }

      console.log('Configuration deleted successfully');
      
      // Clear configuration cache since we've deleted a config
      this._clearConfigCache();
    } catch (error) {
      console.error('Error deleting configuration:', error);
      throw error;
    }
  },

  async validateFolder(request: { folder_id: string }): Promise<DatabaseFolderValidationResult> {
    try {
      console.log('Validating folder...', request.folder_id);
      
      const options = getFetchOptions('POST', request);
      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/validate-folder`, options);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to validate folder: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`Failed to validate folder: ${errorText}`);
      }

      const data = await response.json();
      console.log('Folder validation result:', data);
      return data;
    } catch (error) {
      console.error('Error validating folder:', error);
      throw error;
    }
  },

  async getAccessibleFolders(): Promise<FolderInfo[]> {
    try {
      const options = getFetchOptions();
      const response = await fetchWithAuth(`${API_BASE_URL}/user-config/my-folders`, options);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response getting accessible folders:', response.status, errorText);
        throw new Error(`Failed to get accessible folders: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Accessible folders loaded:', data);
      return data;
    } catch (error) {
      console.error('Error getting accessible folders:', error);
      throw error;
    }
  },

  async getMyFolders(): Promise<FolderInfo[]> {
    // Alias for getAccessibleFolders for backward compatibility
    return this.getAccessibleFolders();
  }
};

export const LangChainService = {
  async askQuestion(query: string): Promise<QAResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/langchain/qa`, getFetchOptions('POST', { query }));

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to ask question: ${errorText}`);
    }

    return response.json();
  },



  async streamingQA(query: string): Promise<ReadableStream> {
    const response = await fetchWithAuth(`${API_BASE_URL}/langchain/qa/stream`, getFetchOptions('POST', { query }));

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to start streaming QA: ${errorText}`);
    }

    if (!response.body) {
      throw new Error('No response body for streaming');
    }

    return response.body;
  }
}; 